// "Compare with measurement": a measured G(V) or G(V, X) from a CSV next to the computed one, the gap edges of both,
// and fits of the edge against the sweep (critical field Xc, or a linear Zeeman shift and its effective g).
import { Alert, FileButton, Grid, Group, Button, NumberInput, Stack, Table, Text } from "@mantine/core";
import { useMemo, useState } from "react";
import { Plot } from "../../components/Plot";
import { conductanceFigure } from "../../lib/sc";
import { edgesFigure, fitClosing, fitLine, gapEdges, gFromSlope, mapHeatmap, parseMeasurement, type Measurement } from "../../lib/scCompare";

export function ScCompare({ r }: { r: Record<string, any> }) {
  const [raw, setM] = useState<Measurement | null>(null);
  const [offset, setOffset] = useState(0);              // mV: the measured bias minus the true one (SC4)
  const m = useMemo(() => (raw && offset ? { ...raw, V: raw.V.map((v) => v - offset) } as Measurement : raw), [raw, offset]);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const isMap = Array.isArray(r.X);
  const load = (file: File | null) => {
    if (!file) return;
    setName(file.name);
    file.text().then((t) => {
      try {
        setM(parseMeasurement(t));
        setError("");
      } catch (e) {
        setM(null);
        setError(e instanceof Error ? e.message : String(e));
      }
    });
  };
  const G = (r.temperature_K > 0 && r.G_T ? r.G_T : r.G) as number[] | number[][];
  const view = useMemo(() => {
    if (!m) return null;
    if (!isMap || m.kind === "curve") {
      const meas = m.kind === "curve" ? m : { V: m.V, G: m.G[0] };
      const row = isMap ? (G as number[][])[0] : (G as number[]);
      return { curve: conductanceFigure({ ...r, G: row, G_T: row, R_he: undefined, measured: meas, name: "Computed and measured G(V)" }) };
    }
    const zmax = Math.max(...(G as number[][]).flat().filter(Number.isFinite), ...m.G.flat().filter(Number.isFinite));
    const edC = gapEdges(r.V, G as number[][]), edM = gapEdges(m.V, m.G);
    const fitC = fitClosing(r.X, edC), fitM = fitClosing(m.X, edM);
    const lineC = fitLine(r.X, edC), lineM = fitLine(m.X, edM);
    return {
      computed: mapHeatmap({ X: r.X, V: r.V, G: G as number[][] }, "Computed", r.label ?? r.variable, zmax),
      measured: mapHeatmap(m, `Measured (${name})`, r.label ?? r.variable, zmax),
      edges: edgesFigure([{ name: "computed", X: r.X, E: edC, color: "#2e213e", fit: fitC },
        { name: "measured", X: m.X, E: edM, color: "#1baf7a", fit: fitM }], r.label ?? r.variable),
      rows: [["computed", fitC, lineC], ["measured", fitM, lineM]] as const,
    };
  }, [m, r, G, isMap, name]);
  return (
    <Stack gap="xs" data-testid="sc-compare">
      <Group gap="xs">
        <FileButton onChange={load} accept=".csv,.txt,.dat,.tsv">{(props) => <Button size="xs" variant="light" {...props}>Open a measured CSV…</Button>}</FileButton>
        {name && <Text size="xs" c="dimmed">{name}</Text>}
        <NumberInput size="xs" w={200} label="bias offset of the measurement" rightSection={<Text size="xs" c="dimmed">mV</Text>}
          rightSectionWidth={36} step={0.01} decimalScale={4} value={offset} onChange={(v) => setOffset(typeof v === "number" ? v : Number(v) || 0)}
          data-testid="sc-bias-offset" />
      </Group>
      <Text size="xs" c="dimmed">A map: first row a label and the bias values (mV), then one row per {isMap ? (r.label ?? r.variable) : "sweep value"} with its
        dI/dV (2e²/h); or three columns X, V, G; a single curve: two columns V, G. The computed map's own CSV has this form. A bias offset
        (an amplifier or thermovoltage offset) is taken off the measured bias before comparing and fitting. The file stays in your browser.</Text>
      {error && <Alert color="red" p="xs">{error}</Alert>}
      {view?.curve && <Plot data={view.curve.data} layout={view.curve.layout} height={380} />}
      {view?.computed && (
        <>
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}><Plot data={view.computed.data} layout={view.computed.layout} height={360} /></Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}><Plot data={view.measured.data} layout={view.measured.layout} height={360} /></Grid.Col>
          </Grid>
          <Plot data={view.edges.data} layout={view.edges.layout} height={340} />
          <Table fz="xs" data-testid="sc-fit-table">
            <Table.Thead><Table.Tr><Table.Th /><Table.Th>edge at X = 0, E0 (mV)</Table.Th><Table.Th>closing Xc</Table.Th>
              <Table.Th>rms (mV)</Table.Th><Table.Th>linear slope (mV / unit)</Table.Th>{r.variable === "B" && <Table.Th>g_eff (linear)</Table.Th>}</Table.Tr></Table.Thead>
            <Table.Tbody>
              {view.rows.map(([who, f, l]) => (
                <Table.Tr key={who}>
                  <Table.Td>{who}</Table.Td>
                  <Table.Td>{f ? f.E0.toFixed(4) : "-"}</Table.Td>
                  <Table.Td>{f ? `${f.Xc.toPrecision(4)} ${r.unit ?? ""}` : "-"}</Table.Td>
                  <Table.Td>{f ? f.rms.toFixed(4) : "-"}</Table.Td>
                  <Table.Td>{l ? l.b.toPrecision(4) : "-"}</Table.Td>
                  {r.variable === "B" && <Table.Td>{l ? gFromSlope(l.b).toPrecision(3) : "-"}</Table.Td>}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Text size="xs" c="dimmed">The edge is the bias of the largest dI/dV at V &gt; 0 in each row. E0·√(1 − (X/Xc)²) describes a gap suppressed by
            a critical field Xc (the contacts' B_c, or a quadratic closing); a straight line a Zeeman shift (g_eff = −2·slope/μB for B in tesla
            and the edge in mV). Compare Xc along x, y and z, or the angle maps, to choose the g-tensor and B_c of step 4.</Text>
        </>
      )}
    </Stack>
  );
}
